import React from 'react';
import { Info } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import PassengerType from 'components/ui/PassengerType';
import { Row, Column } from 'components/shared/Layout';
import { Cell, TableRow, Table, TableHeader } from 'components/ui/StyledTable';
import Checkbox from 'components/ui/Checkbox';

import { SaveButton, ExitButton, StyledTableInputWrapper, StyledTableInput, DependantTooltip } from '../../Passengers.styles';

const SuspendForm = ({ passengers, columnSpan, toggleEditMode, onSave, isTableInEditMode }) => {
  const { reset, handleSubmit, control, register } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      passengers: passengers.map(
        ({
          passengerId,
          firstName,
          lastName,
          type,
          canSuspendRestore,
          isDependant,
          isSuspend,
          disabilityType,
          confirmedTickets,
          suspendedTickets
        }) => ({
          passengerId,
          firstName,
          lastName,
          type,
          canSuspendRestore,
          isDependant,
          disabilityType,
          isSuspend,
          confirmedTickets,
          suspendedTickets
        })
      )
    }
  });

  const { fields } = useFieldArray({
    control,
    name: 'passengers'
  });

  const handleSuspendRestoreExit = () => {
    reset({ passengers });
    toggleEditMode();
  };

  const onSubmit = (formData) => {
    const ticketsToConfirm = [];
    const ticketsToSuspend = [];

    passengers.forEach(({ isSuspend, confirmedTickets, suspendedTickets }, index) => {
      const { isSuspend: formSuspend } = formData.passengers[index];

      if (isSuspend !== formSuspend) {
        if (formSuspend) {
          ticketsToSuspend.push(...confirmedTickets);
        } else {
          ticketsToConfirm.push(...suspendedTickets);
        }
      }
    });

    if (ticketsToConfirm.length > 0 || ticketsToSuspend.length > 0) {
      onSave({
        ticketsToConfirm,
        ticketsToSuspend
      });
    }

    toggleEditMode();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row data-cy="passengers-suspend-controls" style={{ direction: 'rtl' }}>
        {isTableInEditMode && (
          <>
            <Column span={2}>
              <SaveButton type="submit" data-cy="save-passengers" data-testid="save-passengers">
                <FormattedMessage id="buttons.save" />
              </SaveButton>
            </Column>
            <Column span={2}>
              <ExitButton type="button" data-cy="exit-passengers" onClick={handleSuspendRestoreExit}>
                <FormattedMessage id="buttons.exit" />
              </ExitButton>
            </Column>
          </>
        )}
      </Row>
      <Table data-cy="passengers-suspend-table">
        <TableHeader columns={columnSpan}>
          <Cell span={1}>
            <FormattedMessage id="passengerDetails.passengerId" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.firstName" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.lastName" />
          </Cell>
          <Cell span={1}>
            <FormattedMessage id="passengerDetails.paxType" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.suspend" />
          </Cell>
        </TableHeader>

        <div>
          {fields.map(({ passengerId, firstName, lastName, type, canSuspendRestore, isDependant, disabilityType }, index) => (
            <TableRow columns={columnSpan} key={passengerId} data-cy="passengers-table-row">
              <Cell span={1} alignItems="center">
                {passengerId.split('_')?.[1] || passengerId}
              </Cell>
              <Cell span={2} alignItems="center">
                <StyledTableInputWrapper>
                  <StyledTableInput
                    type="text"
                    defaultValue={firstName}
                    disabled
                    {...register(`passengers.${index}.firstName`, {
                      required: false
                    })}
                  />
                </StyledTableInputWrapper>
              </Cell>
              <Cell span={2} alignItems="center">
                <StyledTableInputWrapper>
                  <StyledTableInput
                    type="text"
                    defaultValue={lastName}
                    disabled
                    {...register(`passengers.${index}.lastName`, {
                      required: false
                    })}
                  />
                </StyledTableInputWrapper>
              </Cell>
              <Cell span={1} alignItems="center">
                <DependantTooltip>
                  <PassengerType type={type} disabilityType={disabilityType} />
                  {isDependant && (
                    <>
                      &nbsp;
                      <Info size={15} data-for="dependant-tooltip" data-tip />
                    </>
                  )}
                </DependantTooltip>
                <ReactTooltip className="tooltip" id="dependant-tooltip" data-cy="dependant-tooltip" place="bottom">
                  <FormattedMessage id="paxType.dependant" />
                </ReactTooltip>
              </Cell>
              <Cell span={2} alignItems="center">
                <Controller
                  name={`passengers.${index}.isSuspend`}
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Checkbox
                      dataCy="passenger-suspended"
                      name={name}
                      checked={value}
                      disabled={!canSuspendRestore}
                      onChange={(checked) => onChange(checked)}
                    />
                  )}
                />
              </Cell>
            </TableRow>
          ))}
        </div>
      </Table>
    </form>
  );
};

SuspendForm.propTypes = {
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      passengerId: PropTypes.string.isRequired,
      isDependant: PropTypes.bool,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      birthDate: PropTypes.string,
      email: PropTypes.string,
      phoneNumber: PropTypes.string,
      type: PropTypes.string,
      loyaltyNumber: PropTypes.string,
      isPassengerEditable: PropTypes.bool,
      isTravelDocumentRequired: PropTypes.bool,
      hasTravelDocumentBeenProvided: PropTypes.bool
    }).isRequired
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  isTableInEditMode: PropTypes.bool.isRequired,
  columnSpan: PropTypes.number.isRequired,
  toggleEditMode: PropTypes.func.isRequired
};

export default SuspendForm;
